export function formatFeedbackDisplayName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\b\w+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
}

export function normalizeFeedbackQuestionType(question) {
  const explicit = String(question?.type || '').trim().toLowerCase();
  if (explicit) {
    return explicit;
  }
  if (question?.id === 'skill_ratings') {
    return 'skill_matrix';
  }
  if (Array.isArray(question?.skills) && question.skills.length) {
    return 'skill_matrix';
  }
  if (Array.isArray(question?.options) && question.options.length) {
    return 'single_choice';
  }
  return 'text';
}

export function inferFeedbackQuestionType(item, questions = []) {
  const direct = normalizeFeedbackQuestionType(item);
  if (direct !== 'text' || item?.type) {
    return direct;
  }
  const match = (questions || []).find((question) => question?.id === item?.id);
  if (match) {
    return normalizeFeedbackQuestionType(match);
  }
  const raw = String(item?.answer || '').trim();
  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return 'skill_matrix';
      }
    } catch {
      // ignore invalid json
    }
  }
  return 'text';
}

export function feedbackQuestionUsesVoiceInput(question) {
  return normalizeFeedbackQuestionType(question) === 'text';
}

export function feedbackAnswersById(answers) {
  const mapping = new Map();
  (answers || []).forEach((item) => {
    if (item?.id) {
      mapping.set(item.id, item);
    }
  });
  return mapping;
}

export function feedbackQuestionApplies(question, answers) {
  const showWhen = question?.show_when;
  if (!showWhen?.question_id) {
    return true;
  }
  const answersMap = feedbackAnswersById(answers);
  const refAnswer = String(answersMap.get(showWhen.question_id)?.answer || '').trim();
  return (showWhen.values || []).includes(refAnswer);
}

export function filterVisibleFeedbackQuestions(questions, answers) {
  return (questions || []).filter((question) => feedbackQuestionApplies(question, answers));
}

export function parseSkillMatrixAnswer(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return { ...raw };
  }
  const text = String(raw || '').trim();
  if (!text) {
    return {};
  }
  if (text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  const parsed = {};
  text.split('\n').forEach((line) => {
    const [skill, rating] = String(line).split(':').map((part) => part.trim());
    if (skill && rating) {
      parsed[skill] = rating;
    }
  });
  return parsed;
}

export function serializeSkillMatrixAnswer(values) {
  return JSON.stringify(values || {});
}

export function formatSkillMatrixAnswerDisplay(raw) {
  const parsed = parseSkillMatrixAnswer(raw);
  const entries = Object.entries(parsed).filter(([, rating]) => String(rating || '').trim());
  if (!entries.length) {
    return '';
  }
  return entries.map(([skill, rating]) => `${skill}: ${rating}`).join('\n');
}

export function formatFeedbackAnswerForDisplay(item, questions = []) {
  if (!item) {
    return '';
  }
  const questionType = inferFeedbackQuestionType(item, questions);
  const answer = item.answer;
  if (questionType === 'skill_matrix') {
    return formatSkillMatrixAnswerDisplay(answer);
  }
  return String(answer || '').trim();
}

export function validateFeedbackStep(question, { draftAnswer, skillDraft } = {}) {
  if (!question?.required) {
    return '';
  }
  const questionType = normalizeFeedbackQuestionType(question);
  if (questionType === 'skill_matrix') {
    const skills = question.skills || [];
    const missing = skills.filter((skill) => !String(skillDraft?.[skill] || '').trim());
    if (missing.length) {
      return `Please rate: ${missing.join(', ')}.`;
    }
    return '';
  }
  if (!String(draftAnswer || '').trim()) {
    return `Please answer: ${question.label || question.prompt || 'this question'}.`;
  }
  return '';
}

export function mergeFeedbackStepAnswer(sessionAnswers, question, answerValue, questionType) {
  return (sessionAnswers || []).map((item) =>
    item.id === question?.id
      ? { ...item, answer: answerValue, type: questionType || item.type || 'text' }
      : item
  );
}

export function resolveFeedbackStepDraftValues(question, { draftAnswer, skillDraft } = {}) {
  const questionType = normalizeFeedbackQuestionType(question);
  if (questionType === 'skill_matrix') {
    return {
      questionType,
      answerValue: serializeSkillMatrixAnswer(skillDraft),
      draftAnswer: '',
      skillDraft: skillDraft || {},
    };
  }
  return {
    questionType,
    answerValue: String(draftAnswer || '').trim(),
    draftAnswer: String(draftAnswer || '').trim(),
    skillDraft: {},
  };
}

export function validateAllFeedbackAnswers(questions, answers, activeStep = {}) {
  const answersById = feedbackAnswersById(answers);
  const visible = filterVisibleFeedbackQuestions(questions, answers);
  for (const question of visible) {
    const isActive = question.id === activeStep.questionId;
    const questionType = normalizeFeedbackQuestionType(question);
    const savedAnswer = answersById.get(question.id)?.answer ?? question.answer ?? '';
    const error = validateFeedbackStep(question, {
      draftAnswer: isActive ? activeStep.draftAnswer : savedAnswer,
      skillDraft: isActive && questionType === 'skill_matrix'
        ? activeStep.skillDraft
        : parseSkillMatrixAnswer(savedAnswer),
    });
    if (error) {
      return error;
    }
  }
  return '';
}

export function clearConditionalFeedbackAnswers(answers, decisionValue) {
  const nextDecision = String(decisionValue || '').trim();
  const showNextStep = ['Proceed to Next Round', 'Shortlist'].includes(nextDecision);
  const showRejectReason = nextDecision === 'Reject';
  return (answers || []).map((item) => {
    if (item.id === 'next_step' && !showNextStep) {
      return { ...item, answer: '' };
    }
    if (item.id === 'reject_reason' && !showRejectReason) {
      return { ...item, answer: '' };
    }
    return item;
  });
}
